// useRemoteControl — TV-side composable.
// Opens a WebSocket to /ws/remote/<sessionId>?role=tv, listens for command
// messages from the paired phone, and dispatches them as synthetic
// keyboard events so the existing spatial-nav composable handles them
// without any wiring changes.
//
// Wire format (JSON over WS):
//   Phone → TV
//     { type: 'key', key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'
//                   | 'Enter' | 'Escape' | 'Backspace' }
//     { type: 'media', action: 'play_pause' | 'seek_forward' | 'seek_back' }
//     { type: 'voice_search' }
//     { type: 'cursor', dx, dy }             — relative trackpad delta
//     { type: 'cursor_click' }               — tap on trackpad
//     { type: 'scroll', dx, dy }             — two-finger scroll delta
//     { type: 'pong' }                       — keepalive ack
//   TV → Phone
//     { type: 'tv_ready' }                   — sent on phone connect
//     { type: 'phone_connected' }
//     { type: 'session_ended', reason }      — final, before close
//     { type: 'state', route: '/...', tv: true } (future)

import { ref } from 'vue'
import { apiFetch } from '@/services/apiClient'

// Module-singleton state. Multiple components (AccountMenu trigger,
// RemotePairModal, an "End session" button somewhere else) share the
// SAME session. Closing the modal must not tear down the session, so we
// can't tie the WS to a component's onUnmounted lifecycle. The session
// lives until the user explicitly calls stop(), or the page reloads.
const sessionId = ref('')
const phoneConnected = ref(false)
const status = ref('idle') // idle | starting | ready | reconnecting | error
const errorMessage = ref('')
let ws = null
// Reconnect bookkeeping. The TV-side WS can drop on a Vue Router navigation
// (HMR in dev, the page transition causing a transient close, etc) or on a
// brief network blip. The session is meant to outlive any single page, so
// we hold the sessionId and retry attaching to it indefinitely until the
// user explicitly stops or the server returns 'session_ended'. Backoff is
// capped at 30s so a long outage doesn't busy-loop, but we never give up.
// While either side is connected the server treats the session as in-use,
// so the only hard fail is the server actively closing the WS with a
// 'session_ended' frame.
let reconnectTimer = null
let reconnectAttempts = 0
// User-initiated stop sets this so the close handler doesn't try to
// reconnect on top of the user's "End session" intent.
let intentionalClose = false
// Set when the server sends 'session_ended' — the session is dead and
// retrying won't bring it back, so the close handler should not retry.
let serverEnded = false

export function useRemoteControl() {

  // The QR encodes a fully-qualified URL so the phone can navigate from a
  // camera scanner on a different network — building it from window.origin
  // keeps it correct for both dev (localhost:5173) and prod (custom domain).
  function buildPairUrl(id) {
    if (!id) return ''
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/remote/${id}`
  }

  async function start() {
    if (status.value === 'starting' || status.value === 'ready' || status.value === 'reconnecting') return
    // Fresh start — clear any state left over from a previous session
    // that was stopped or expired.
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
    reconnectAttempts = 0
    intentionalClose = false
    serverEnded = false
    status.value = 'starting'
    errorMessage.value = ''
    phoneConnected.value = false

    try {
      const data = await apiFetch('/api/remote/session', { method: 'POST', json: {} })
      sessionId.value = data.sessionId
      openSocket(data.sessionId)
    } catch (err) {
      console.warn('[remote] failed to create session:', err)
      status.value = 'error'
      errorMessage.value = err?.body?.error || err?.message || 'Failed to create remote session'
    }
  }

  function openSocket(id) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${proto}//${window.location.host}/ws/remote/${id}?role=tv`
    ws = new WebSocket(url)

    ws.addEventListener('open', () => {
      status.value = 'ready'
      // Successful connect — reset the retry counter so a future drop gets
      // the full retry budget again.
      reconnectAttempts = 0
    })
    ws.addEventListener('message', (event) => {
      handleIncoming(event.data)
    })
    ws.addEventListener('close', () => {
      ws = null
      // Intentional stop() — leave state as 'idle' and don't retry.
      if (intentionalClose) {
        intentionalClose = false
        status.value = 'idle'
        phoneConnected.value = false
        return
      }
      // Server told us the session is over (e.g. another tab ended it,
      // session expired, attach_failed). Retrying won't recover.
      if (serverEnded) {
        serverEnded = false
        status.value = 'idle'
        phoneConnected.value = false
        sessionId.value = ''
        return
      }
      // Unexpected close. Reconnect to the same session indefinitely
      // with exponential backoff capped at 30s. The server holds a 30s
      // grace before tearing down a TV-disconnected session, so the
      // first few retries always land within grace; later retries pick
      // up if the network comes back.
      if (sessionId.value) {
        status.value = 'reconnecting'
        reconnectAttempts += 1
        const delay = Math.min(500 * 2 ** (reconnectAttempts - 1), 30_000)
        if (reconnectTimer) clearTimeout(reconnectTimer)
        reconnectTimer = setTimeout(() => {
          if (!sessionId.value) return
          openSocket(sessionId.value)
        }, delay)
        return
      }
      // No sessionId — can't retry.
      status.value = 'idle'
      phoneConnected.value = false
    })
    ws.addEventListener('error', () => {
      // The 'close' handler runs right after, so we just record the
      // error message here and let close handle the state transition.
      errorMessage.value = 'Connection lost'
    })
  }

  function handleIncoming(raw) {
    let msg
    try { msg = JSON.parse(raw) } catch { return }
    if (!msg || typeof msg !== 'object') return

    switch (msg.type) {
      case 'phone_connected':
        phoneConnected.value = true
        return
      case 'session_ended':
        phoneConnected.value = false
        // The server is closing this WS right after — don't reconnect.
        serverEnded = true
        return
      case 'key':
        dispatchKey(msg.key)
        return
      case 'media':
        applyMediaAction(msg.action)
        return
      case 'voice_search':
        // Spatial-nav already binds the mic icon to a custom event so that
        // App.vue can reach it from anywhere. Fall back to a synthetic key
        // if the event listener isn't installed.
        window.dispatchEvent(new CustomEvent('remote-voice-search'))
        return
      case 'text':
        applyText(msg.text || '', { clear: !!msg.clear })
        return
      case 'navigate':
        applyNavigate(msg.path)
        return
      case 'cursor':
        // Phone trackpad in cursor mode — forward the delta to the cursor
        // overlay component. RemoteCursor.vue owns its own position state
        // and scaling so this composable stays thin.
        window.dispatchEvent(new CustomEvent('remote-cursor-move', {
          detail: { dx: Number(msg.dx) || 0, dy: Number(msg.dy) || 0 },
        }))
        return
      case 'cursor_click':
        window.dispatchEvent(new CustomEvent('remote-cursor-click'))
        return
      case 'scroll':
        applyScroll(Number(msg.dx) || 0, Number(msg.dy) || 0)
        return
      default:
        // ignore unknown messages — phones from a future version may send
        // commands the TV doesn't understand yet
        return
    }
  }

  // Whitelist accepted key names so an attacker who somehow gets the
  // session URL can't synthesize arbitrary keystrokes (Tab, F5, etc.).
  const ALLOWED_KEYS = new Set([
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Enter', 'Escape', 'Backspace',
  ])

  function dispatchKey(key) {
    if (!ALLOWED_KEYS.has(key)) return
    const ev = new KeyboardEvent('keydown', {
      key,
      code: key,
      bubbles: true,
      cancelable: true,
    })
    document.dispatchEvent(ev)
  }

  // Pick the field the phone's text should land in. Priority:
  //   1. The currently-focused input/textarea/contenteditable
  //   2. A visible search input (input[type=search] or [role=searchbox])
  //   3. The first text input in the document
  // Returns null if nothing matches — caller swallows the message rather
  // than dispatching to a random unrelated control.
  function pickTextTarget() {
    const active = document.activeElement
    if (active && isTextEditable(active)) return active

    const search = document.querySelector(
      'input[type="search"]:not([disabled]), input[role="searchbox"]:not([disabled])',
    )
    if (search) return search

    const firstInput = document.querySelector(
      'input[type="text"]:not([disabled]), textarea:not([disabled]), [contenteditable="true"]',
    )
    return firstInput || null
  }

  function isTextEditable(el) {
    if (!el) return false
    const tag = el.tagName
    if (tag === 'TEXTAREA') return !el.disabled && !el.readOnly
    if (tag === 'INPUT') {
      const t = (el.type || 'text').toLowerCase()
      return ['text', 'search', 'url', 'email', 'tel', 'password'].includes(t)
        && !el.disabled && !el.readOnly
    }
    return el.isContentEditable
  }

  function applyText(text, { clear = false } = {}) {
    const target = pickTextTarget()
    if (!target) return
    if (target.isContentEditable) {
      // contenteditable: replace selection or append at end.
      try {
        target.focus()
        if (clear) {
          target.textContent = ''
        } else {
          // execCommand is technically deprecated but still the only API
          // that fires the 'input' event reliably across all browsers.
          document.execCommand('insertText', false, text)
        }
      } catch { /* best-effort */ }
      return
    }
    // input/textarea: use setRangeText so the underlying form library sees
    // an 'input' event and re-runs validation/handlers.
    try {
      target.focus()
      if (clear) {
        target.value = ''
      } else {
        // Append at the end of whatever's already there. Most users want
        // the phone-typed text to extend rather than replace the field.
        const start = target.value.length
        target.setRangeText(text, start, start, 'end')
      }
      target.dispatchEvent(new Event('input', { bubbles: true }))
      target.dispatchEvent(new Event('change', { bubbles: true }))
    } catch { /* best-effort */ }
  }

  // Phone can request the TV navigate to a route — used by the
   // in-remote library browser. Whitelist the path prefixes the phone
   // is allowed to push so a misbehaving (or malicious) WS peer can't
   // jump the TV into /admin/* or /settings.
  function applyNavigate(path) {
    if (typeof path !== 'string' || !path.startsWith('/')) return
    // Phone-side browse tabs route into these areas. Anything outside
    // is blocked so a misbehaving (or hostile) WS peer can't jump the
    // TV into /admin, /settings, etc.
    const allowedPrefixes = [
      '/browse',     // TMDB title detail (Discover + Watchlist pick into here)
      '/discover',   // /discover and /discover/movies / /discover/tv / /discover/watchlist
      '/library',    // Put.io file browser + /library/downloads
      '/media',      // Put.io library + folders + files
      '/tv',         // Cinematic /tv homepage
      '/watchlist',  // Back-compat alias for /discover/watchlist
      '/downloads',  // Back-compat alias for /library/downloads
      '/live-tv',    // Live TV catalogue + player
      '/youtube',    // YouTube channel detail + playback
    ]
    const ok = allowedPrefixes.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'))
    if (!ok) return
    // Lazy-load the router to avoid a circular dependency on services
    // that already depend on this composable.
    import('@/router').then(({ default: router }) => {
      try { router.push(path) } catch { /* ignore */ }
    }).catch(() => { /* ignore */ })
  }

  // Two-finger scroll from the phone. Synthetic 'wheel' events don't
  // actually scroll in Chromium, so we call scrollBy() on the best
  // scrollable target instead — per axis, so horizontal carousels
  // (Discover shelves, etc.) get their dx and the surrounding page
  // gets its dy from the same gesture.
  //
  // Sign convention: positive dx/dy = native scroll forward (right/down),
  // matching the phone's "fingers and content move together" model.
  const SCROLL_GAIN = 2.5

  function scrollableAncestorOnAxis(el, axis /* 'x' | 'y' */) {
    const overflowKey = axis === 'x' ? 'overflowX' : 'overflowY'
    const sizeKey = axis === 'x' ? 'scrollWidth' : 'scrollHeight'
    const clientKey = axis === 'x' ? 'clientWidth' : 'clientHeight'
    let node = el
    while (node && node !== document.body) {
      if (node.nodeType === 1) {
        const style = getComputedStyle(node)
        const overflow = style[overflowKey]
        const scrollable = (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay')
        if (scrollable && node[sizeKey] - node[clientKey] > 1) return node
      }
      node = node.parentNode
    }
    return null
  }

  function pickHitElement() {
    // Prefer the element under the remote cursor — that's the spatial
    // model users expect from a real trackpad. Falls back to the active
    // element when the cursor overlay isn't visible.
    const cursorEl = document.querySelector('.remote-cursor')
    if (cursorEl && cursorEl.style.display !== 'none' && cursorEl.offsetParent !== null) {
      const rect = cursorEl.getBoundingClientRect()
      return document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
    }
    return document.activeElement || null
  }

  function applyScroll(dx, dy) {
    if (!dx && !dy) return
    const hit = pickHitElement()
    const root = document.scrollingElement || document.documentElement

    if (dx) {
      const amountX = dx * SCROLL_GAIN
      const targetX = (hit ? scrollableAncestorOnAxis(hit, 'x') : null)
      if (targetX) targetX.scrollBy({ left: amountX, behavior: 'auto' })
      // No window-level horizontal fallback: vertical-only pages would
      // silently grow scrollbars and feel broken. If nothing under the
      // cursor scrolls horizontally, the dx is intentionally dropped.
    }
    if (dy) {
      const amountY = dy * SCROLL_GAIN
      const targetY = (hit ? scrollableAncestorOnAxis(hit, 'y') : null)
      if (targetY) {
        targetY.scrollBy({ top: amountY, behavior: 'auto' })
      } else if (root) {
        root.scrollBy({ top: amountY, behavior: 'auto' })
      } else {
        window.scrollBy(0, amountY)
      }
    }
  }

  function applyMediaAction(action) {
    const video = document.querySelector('video')
    if (!video) return
    switch (action) {
      case 'play_pause':
        if (video.paused) video.play().catch(() => {})
        else video.pause()
        return
      case 'seek_back':
        try { video.currentTime = Math.max(0, video.currentTime - 10) } catch { /* ignore */ }
        return
      case 'seek_forward':
        try { video.currentTime = Math.min(video.duration || Infinity, video.currentTime + 10) } catch { /* ignore */ }
        return
    }
  }

  function stop() {
    intentionalClose = true
    serverEnded = false
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
    reconnectAttempts = 0
    if (ws) {
      try { ws.close(1000, 'user_stop') } catch { /* ignore */ }
      ws = null
    }
    sessionId.value = ''
    phoneConnected.value = false
    status.value = 'idle'
    errorMessage.value = ''
  }

  return {
    sessionId,
    pairUrl: () => buildPairUrl(sessionId.value),
    phoneConnected,
    status,
    errorMessage,
    start,
    stop,
  }
}
